import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux';
import {
  PLAN_FEATURES,
  PLAN_LIMITS,
  PLAN_RANK,
  type PlanLimits,
  type PlanTier,
} from '@/redux/slices/planSlice';

export function usePlan() {
  const planSlice = useSelector((state: RootState) => state.plan);

  const tier = planSlice.tier;
  const expiresAt = planSlice.expiresAt;
  const trialDaysLeft = planSlice.trialDaysLeft;
  const graceDaysLeft = planSlice.graceDaysLeft;
  const usage = planSlice.usage;
  const isLoaded = planSlice.isLoaded;

  const can = (feature: string): boolean => {
    // Ưu tiên features từ API (tenant.features), fallback hardcoded mapping
    const apiFeatures = planSlice.features ?? [];
    const tierFeatures = PLAN_FEATURES[tier] ?? [];
    if (apiFeatures.length > 0) {
      // Feature được granted trực tiếp (hoặc '*' wildcard)
      if (apiFeatures.includes('*')) return true;
      if (apiFeatures.includes(feature)) return true;
      // Fallback: feature không có trong apiFeatures nhưng có trong tier → allow
      // (API chỉ trả explicit features, không phải full tier feature list)
      return tierFeatures.includes('*') || tierFeatures.includes(feature);
    }
    return tierFeatures.includes('*') || tierFeatures.includes(feature);
  };

  const atLeast = (minTier: PlanTier): boolean => PLAN_RANK[tier] >= PLAN_RANK[minTier];

  const limits: PlanLimits = PLAN_LIMITS[tier];

  const isOverLimit = (key: keyof PlanLimits): boolean => {
    const limit = limits[key];
    if (limit === -1) return false;
    return usage[key] >= limit;
  };

  const usagePercent = (key: keyof PlanLimits): number => {
    const limit = limits[key];
    if (limit === -1) return 0;
    return Math.min(100, Math.round((usage[key] / limit) * 100));
  };

  const inTrial = (trialDaysLeft ?? 0) > 0;
  const inGrace = (graceDaysLeft ?? 0) > 0;

  return useMemo(
    () => ({
      tier,
      expiresAt,
      trialDaysLeft,
      graceDaysLeft,
      usage,
      limits,
      inTrial,
      inGrace,
      isLoaded,
      can,
      atLeast,
      isOverLimit,
      usagePercent,
    }),
    [tier, expiresAt, trialDaysLeft, graceDaysLeft, usage, limits, inTrial, inGrace, isLoaded],
  );
}
